import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface ThoughtSpotOrganization {
    id: number;
    name: string;
}

export interface Connections {
    id: number;
    name: string;
}

interface IntegrationState {
    currentStep: number;
    stepsCompletedCount: number;
    selectedTarget: string | null;
    connectionCreated: boolean;
    org_list: ThoughtSpotOrganization[];
    stackFlowStatus: string;
    selectedBiPlatform: string | null;
    selectedDatabasePlatform: string | null;
    biPlatformConnections: Connections[];
    databaseConnections: Connections[];
    showBiPlatform: boolean;
    isSubmitDisabled: boolean;
    isEditFlow: boolean;
    selectedConnection: any;
    integrationType: string | null;
    selectedStack: any;
}

const initialState: IntegrationState = {
    currentStep: 0,
    stepsCompletedCount: 0,
    selectedTarget: null,
    connectionCreated: false,
    org_list: [],
    stackFlowStatus: "database_in_progress",
    selectedBiPlatform: null,
    selectedDatabasePlatform: null,
    biPlatformConnections: [],
    databaseConnections: [],
    showBiPlatform: false,
    isSubmitDisabled: true,
    isEditFlow: false,
    selectedConnection: null,
    integrationType: null,
    selectedStack: null,
};

const integrationSlice = createSlice({
    name: "integration",
    initialState,
    reducers: {
        setCurrentStep(state, action: PayloadAction<number>) {
            state.currentStep = action.payload;
        },
        nextStep(state) {
            state.currentStep += 1;
        },
        prevStep(state) {
            state.currentStep -= 1;
        },
        setStepsCompletedCount(state, action: PayloadAction<number>) {
            state.stepsCompletedCount = action.payload;
        },
        setSelectedTarget(state, action: PayloadAction<string | null>) {
            state.selectedTarget = action.payload;
        },
        setConnectionCreated(state, action: PayloadAction<boolean>) {
            state.connectionCreated = action.payload;
        },
        setOrglist(state, action: PayloadAction<ThoughtSpotOrganization[]>) {
            state.org_list = action.payload;
        },
        setStackFlowStatus(state, action: PayloadAction<string>) {
            state.stackFlowStatus = action.payload;
        },
        setSelectedBiPlatform(state, action: PayloadAction<string | null>) {
            state.selectedBiPlatform = action.payload;
        },
        setSelectedDatabasePlatform(state, action: PayloadAction<string | null>) {
            state.selectedDatabasePlatform = action.payload;
        },
        setBiPlatformConnections(state, action: PayloadAction<Connections[]>) {
            state.biPlatformConnections = action.payload;
        },
        setDatabaseConnections(state, action: PayloadAction<Connections[]>) {
            state.databaseConnections = action.payload;
        },
        setShowBiPlatform(state, action: PayloadAction<boolean>) {
            state.showBiPlatform = action.payload;
        },
        setSubmitDisabled(state, action: PayloadAction<boolean>) {
            state.isSubmitDisabled = action.payload;
        },
        setEditFlow(state, action: PayloadAction<boolean>) {
            state.isEditFlow = action.payload;
        },
        setSelectedConnection(state, action: PayloadAction<Connections | null>) {
            state.selectedConnection = action.payload;
        },
        setIntegrationType(state, action: PayloadAction<string | null>) {
            state.integrationType = action.payload;
        },
        setSelectedStack(state, action: PayloadAction<any>) {
            state.selectedStack = action.payload;
        },
        resetIntegrationState() {
            return initialState;
        },
    },
});

export const {
    setCurrentStep,
    nextStep,
    prevStep,
    setSelectedTarget,
    setStepsCompletedCount,
    setConnectionCreated,
    setOrglist,
    setStackFlowStatus,
    setSelectedBiPlatform,
    setSelectedDatabasePlatform,
    setBiPlatformConnections,
    setDatabaseConnections,
    setShowBiPlatform,
    setSubmitDisabled,
    setEditFlow,
    setSelectedConnection,
    setIntegrationType,
    setSelectedStack,
    resetIntegrationState,
} = integrationSlice.actions;

export default integrationSlice.reducer;